// Shared formatting for the Asset Management pages.

export const MACHINE_STATUS = {
  running: { label: "Running", tone: "ok" },
  down: { label: "Down", tone: "bad" },
  maintenance: { label: "Under maintenance", tone: "warn" },
  retired: { label: "Retired", tone: "muted" },
};

export const BREAKDOWN_STATUS = {
  open: { label: "Open", tone: "bad" },
  acknowledged: { label: "In progress", tone: "warn" },
  resolved: { label: "Resolved", tone: "ok" },
};

export const PM_STATE = {
  overdue: { label: "Overdue", tone: "bad" },
  due: { label: "Due", tone: "warn" },
  due_soon: { label: "Due soon", tone: "info" },
  ok: { label: "Scheduled", tone: "muted" },
  unknown: { label: "No meter reading", tone: "muted" },
};

export const WARRANTY_STATE = {
  active: { label: "Under warranty", tone: "ok" },
  expiring: { label: "Expiring soon", tone: "warn" },
  expired: { label: "Expired", tone: "bad" },
  none: { label: "No warranty", tone: "muted" },
};

export const RECORD_TYPES = [
  { value: "preventive", label: "Preventive" },
  { value: "corrective", label: "Corrective" },
  { value: "inspection", label: "Inspection" },
  { value: "calibration", label: "Calibration" },
];

export const WARRANTY_TYPES = [
  { value: "standard", label: "Manufacturer warranty" },
  { value: "extended", label: "Extended warranty" },
  { value: "amc", label: "AMC (maintenance contract)" },
];

export const CRITICALITY = [
  { value: "high", label: "High — stops production" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low — has a backup" },
];

// Files are served behind the access token; an <img> or a link cannot send a
// header, so it travels in the query string.
export function fileUrl(filePath) {
  if (!filePath) return null;
  return `${process.env.REACT_APP_API_UPLOAD_URL || ""}${filePath}?token=${encodeURIComponent(
    localStorage.getItem("accessToken") || "",
  )}`;
}

export function formatWhen(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

/** For <input type="date"> and <input type="datetime-local">, in local time. */
export function toInputDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function toInputDateTime(date = new Date()) {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${toInputDate(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "45m", "2h 15m", "3d 4h". */
export function formatDuration(minutes) {
  if (minutes == null || Number.isNaN(minutes)) return "—";
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h}h ${m % 60}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d}d ${h % 24}h` : `${d}d`;
}

export function formatHours(hours) {
  if (hours == null) return "—";
  return formatDuration(hours * 60);
}

export function formatPercent(ratio) {
  if (ratio == null) return "—";
  return `${(ratio * 100).toFixed(ratio >= 0.9995 ? 0 : 1)}%`;
}

export function formatMoney(value) {
  if (value == null || value === "") return "—";
  return `₹${Number(value).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

export function machineLabel(machine) {
  if (!machine) return "";
  return machine.code && machine.code !== machine.name ? `${machine.name} (${machine.code})` : machine.name;
}

export function pmDueText(schedule) {
  const { due } = schedule;
  if (schedule.intervalType === "hours") {
    if (due.hoursLeft == null) return "Update the machine's running hours";
    const left = Math.round(due.hoursLeft);
    return left > 0 ? `${left} running hours left` : `${Math.abs(left)} running hours over`;
  }
  if (due.daysLeft == null) return "—";
  if (due.daysLeft < 0) return `${Math.abs(due.daysLeft)} day${due.daysLeft === -1 ? "" : "s"} overdue · ${formatDate(schedule.nextDueAt)}`;
  if (due.daysLeft === 0) return "Due today";
  return `In ${due.daysLeft} day${due.daysLeft === 1 ? "" : "s"} · ${formatDate(schedule.nextDueAt)}`;
}

export function intervalText(schedule) {
  const n = schedule.intervalValue;
  return schedule.intervalType === "hours" ? `Every ${n} running hours` : `Every ${n} day${n === 1 ? "" : "s"}`;
}

export function errorMessage(err, fallback) {
  return err?.response?.data?.error || err?.response?.data?.message || fallback;
}
